import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SupportConsole — engineering-only break-glass surface (Cluster F.5).
 *
 * v1 is intentionally minimal (no design polish, no bilingual strings
 * beyond ack labels — this is an internal tool). Three sections:
 *   1. Start break-glass session form
 *   2. Active + recent token list (caller's own)
 *   3. Audit log (caller's own break-glass events)
 *
 * Client-side role gate is UX-only — the server enforces support-role
 * on every endpoint via `_require_support_role`.
 */
export default function SupportConsole() {
  const { user, session, isAuthenticated, loading } = useAuth();

  const [tokens, setTokens] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [busy, setBusy] = useState(false);
  // Server-side role gate is the real one; client-side gate is just
  // a UX bounce so non-support users see a clear message instead of a
  // wall of 403s. We probe the support-tokens endpoint on mount and
  // use the response status to decide what to render.
  const [roleProbe, setRoleProbe] = useState('pending'); // 'pending' | 'allowed' | 'denied'

  // Issuance form state
  const [password, setPassword] = useState('');
  const [targetOrgId, setTargetOrgId] = useState('');
  const [ticketId, setTicketId] = useState('');
  const [reason, setReason] = useState('');
  const [lastIssued, setLastIssued] = useState(null);

  const authHeader = useCallback(() => ({
    Authorization: `Bearer ${session?.access_token}`,
    'Content-Type': 'application/json',
  }), [session]);

  const refresh = useCallback(async () => {
    if (!session?.access_token) return;
    try {
      const [tRes, aRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/support/tokens`, { headers: authHeader() }),
        fetch(`${BACKEND_URL}/api/support/audit-log`, { headers: authHeader() }),
      ]);
      if (tRes.status === 403) {
        setRoleProbe('denied');
        return;
      }
      setRoleProbe('allowed');
      if (tRes.ok) setTokens((await tRes.json()).tokens || []);
      if (aRes.ok) setAuditLog((await aRes.json()).logs || []);
    } catch (_e) { /* network blip */ }
  }, [session, authHeader]);

  useEffect(() => { refresh(); }, [refresh]);

  const issue = async () => {
    if (busy) return;
    setBusy(true);
    setLastIssued(null);
    try {
      const r1 = await fetch(`${BACKEND_URL}/api/support/reauth`, {
        method: 'POST', headers: authHeader(), body: JSON.stringify({ password }),
      });
      if (!r1.ok) {
        const d = await r1.json().catch(() => ({}));
        toast.error(`reauth failed: ${d.detail || r1.status}`);
        return;
      }
      const { reauth_token } = await r1.json();

      const r2 = await fetch(`${BACKEND_URL}/api/support/break-glass`, {
        method: 'POST', headers: authHeader(),
        body: JSON.stringify({
          target_org_id: targetOrgId,
          ticket_id: ticketId,
          reason,
          reauth_token,
        }),
      });
      if (!r2.ok) {
        const d = await r2.json().catch(() => ({}));
        toast.error(`issuance failed: ${d.detail || r2.status}`);
        return;
      }
      const issued = await r2.json();
      setLastIssued(issued);
      setPassword('');
      toast.success(`Token issued — copy the secret now (shown once)`);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (tokenId) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/support/break-glass/${encodeURIComponent(tokenId)}/revoke`, {
        method: 'POST', headers: authHeader(),
      });
      if (r.ok || r.status === 204) {
        toast.success('Token revoked');
        await refresh();
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(`revoke failed: ${d.detail || r.status}`);
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div style={pageStyle} data-testid="support-console-loading">Loading…</div>;
  }
  if (!isAuthenticated) {
    return <div style={pageStyle} data-testid="support-console-not-authorized">Not authorized — sign in required.</div>;
  }
  if (roleProbe === 'pending') {
    return <div style={pageStyle} data-testid="support-console-probing">Checking access…</div>;
  }
  if (roleProbe === 'denied') {
    return (
      <div style={pageStyle} data-testid="support-console-role-denied">
        <h2>Not authorized</h2>
        <p>This page is restricted to support-role users.</p>
        <p style={{ color: '#666', fontSize: 12 }}>Signed in as: {user?.email}</p>
      </div>
    );
  }

  return (
    <div style={pageStyle} data-testid="support-console">
      <h1 style={{ fontSize: 22, marginBottom: 18 }}>Support — Break-glass console</h1>

      {/* Issuance form */}
      <section data-testid="support-issue-form" style={cardStyle}>
        <h2 style={h2Style}>Start a break-glass session</h2>
        <p style={hintStyle}>
          A short-lived (15 min) read-only token bound to a single target org.
          Every read writes one audit row.
        </p>
        <div style={fieldRow}>
          <label style={labelStyle}>Target org id</label>
          <input
            data-testid="support-target-org-id"
            value={targetOrgId} onChange={(e) => setTargetOrgId(e.target.value)}
            placeholder="org_xxxxxxxxxxxx" style={inputStyle}
          />
        </div>
        <div style={fieldRow}>
          <label style={labelStyle}>Ticket id</label>
          <input
            data-testid="support-ticket-id"
            value={ticketId} onChange={(e) => setTicketId(e.target.value)}
            placeholder="TKT-1234" style={inputStyle}
          />
        </div>
        <div style={fieldRow}>
          <label style={labelStyle}>Reason</label>
          <input
            data-testid="support-reason"
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Customer report …" style={inputStyle}
          />
        </div>
        <div style={fieldRow}>
          <label style={labelStyle}>Your password (re-auth)</label>
          <input
            data-testid="support-password"
            type="password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
          />
        </div>
        <button
          data-testid="support-issue-button"
          onClick={issue}
          disabled={busy || !password || !targetOrgId || !ticketId || !reason}
          style={btnStyle(busy)}
        >
          {busy ? 'Issuing…' : 'Issue token'}
        </button>
        {lastIssued && (
          <div data-testid="support-issued-secret" style={secretBoxStyle}>
            <div><strong>token_id:</strong> {lastIssued.token_id}</div>
            <div><strong>secret (shown once):</strong> <code style={{ wordBreak: 'break-all' }}>{lastIssued.secret}</code></div>
            <div><strong>expires_at:</strong> {lastIssued.expires_at}</div>
          </div>
        )}
      </section>

      {/* Active tokens */}
      <section data-testid="support-tokens-list" style={cardStyle}>
        <h2 style={h2Style}>My tokens ({tokens.length})</h2>
        {tokens.length === 0 ? <p style={hintStyle}>No tokens.</p> : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {tokens.map((t) => (
              <li key={t.id} style={rowStyle} data-testid={`support-token-row-${t.id}`}>
                <code>{t.id}</code> · target={t.target_org_id} · ticket={t.ticket_id} ·
                expires {t.expires_at} {t.revoked_at && <span style={{ color: '#a44' }}> · REVOKED {t.revoked_at}</span>}
                {!t.revoked_at && (
                  <button
                    data-testid={`support-revoke-${t.id}`}
                    onClick={() => revoke(t.id)}
                    disabled={busy}
                    style={smallBtnStyle}
                  >
                    Revoke
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Audit log */}
      <section data-testid="support-audit-log" style={cardStyle}>
        <h2 style={h2Style}>Audit log ({auditLog.length})</h2>
        {auditLog.length === 0 ? <p style={hintStyle}>No events.</p> : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
            {auditLog.slice(0, 50).map((r) => (
              <li key={r.id} style={rowStyle}>
                <span style={{ color: '#888' }}>{r.created_at}</span> · {r.event} · {r.details?.path || r.details?.target_org_id || ''}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh', background: '#0a0a0a', color: '#ddd',
  padding: '32px 48px', fontFamily: 'ui-monospace, "SF Mono", monospace',
};
const cardStyle = {
  background: '#141414', border: '1px solid #222', borderRadius: 8,
  padding: '20px 24px', marginBottom: 24,
};
const h2Style = { fontSize: 14, color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 };
const hintStyle = { fontSize: 12, color: '#888', marginBottom: 8 };
const fieldRow = { marginBottom: 8 };
const labelStyle = { display: 'block', fontSize: 11, color: '#999', marginBottom: 2 };
const inputStyle = { width: '100%', background: '#0a0a0a', border: '1px solid #333', color: '#eee', padding: '6px 8px', borderRadius: 4, fontFamily: 'inherit' };
const btnStyle = (disabled) => ({
  background: disabled ? '#222' : '#1e3a8a', color: disabled ? '#666' : '#fff',
  border: '1px solid #2a4cb3', padding: '8px 16px', borderRadius: 4,
  cursor: disabled ? 'not-allowed' : 'pointer', marginTop: 8,
});
const smallBtnStyle = { background: '#3a1010', color: '#f88', border: '1px solid #5a1818', padding: '2px 8px', borderRadius: 3, marginLeft: 8, cursor: 'pointer', fontSize: 11 };
const rowStyle = { padding: '6px 0', borderBottom: '1px solid #1a1a1a', fontSize: 13 };
const secretBoxStyle = { marginTop: 14, background: '#1a1a0a', border: '1px solid #5a4a18', padding: 12, borderRadius: 4, fontSize: 12 };
